import {
	faCircleExclamation,
	faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PaymentDecline } from '@supply-mover-2-0/shared';
import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { Modal } from '../overlays/Modal';

interface PaymentDeclineModalProps {
	decline: PaymentDecline | null;
	onClose: () => void;
}

/**
 * PD-15185 — a rejected payment, presented once and acknowledged deliberately.
 *
 * It was previously an inline block above the Process Payment button, built
 * from four stacked wrappers. Two problems that a dialog fixes: nothing
 * required the operator to take it in, and their next instinct after a
 * rejection is to press the same button again — which on a duplicate rejection
 * is the one thing they must not do.
 *
 * Rendering `decline.message` verbatim is the point of the structured shape.
 * Do not prefix it here; the sentence arrives finished.
 */
export function PaymentDeclineModal({
	decline,
	onClose,
}: PaymentDeclineModalProps): React.ReactElement | null {
	const colors = useThemeColors();
	const { t } = useTranslation();

	if (decline == null) {
		return null;
	}

	// A duplicate rejection where the earlier charge SUCCEEDED is the one case
	// that must read as "stop" rather than "something went wrong" — the
	// customer's money has already moved.
	const customerAlreadyCharged =
		decline.kind === 'duplicate' && decline.priorCharge?.succeeded === true;

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			size="sm"
			title={t(decline.title)}
			actions={[
				{
					label: t('OK'),
					onClick: onClose,
					variant: customerAlreadyCharged ? 'danger' : 'primary',
				},
			]}
		>
			<div className="flex items-start gap-3">
				<FontAwesomeIcon
					icon={
						customerAlreadyCharged ? faCircleExclamation : faTriangleExclamation
					}
					className={`mt-0.5 text-xl shrink-0 ${
						customerAlreadyCharged
							? colors.classes.textDanger
							: colors.classes.textWarning
					}`}
				/>
				<div className="min-w-0 space-y-2">
					<p className={`text-sm ${colors.classes.textPrimary}`}>
						{t(decline.message)}
					</p>

					{decline.priorCharge != null && (
						<p className={`text-xs ${colors.classes.textSecondary}`}>
							{t('Earlier transaction')}: {t('T-')}
							{decline.priorCharge.transactionId}
						</p>
					)}

					{decline.gatewayDetail != null && (
						<p className={`text-xs ${colors.classes.textSecondary}`}>
							{t('Gateway response')}: {t(decline.gatewayDetail)}
						</p>
					)}
				</div>
			</div>
		</Modal>
	);
}

/**
 * Pull the structured decline out of a failed API call.
 *
 * `ApiService` throws an `Error` carrying the parsed response body on
 * `apiError.data`, so a payment surface can ask "was this a gateway decline?"
 * without parsing message text. Returns null for anything else — a network
 * drop or a validation error is not a decline and should not be dressed as
 * one.
 */
export function extractPaymentDecline(error: unknown): PaymentDecline | null {
	if (error == null || typeof error !== 'object') {
		return null;
	}

	// Two clients, two error shapes. The CRM's ApiService hangs the parsed
	// body on `apiError.data`; the storefront's portalFetch throws an ApiError
	// carrying it on `data`. Reading both keeps one extractor for both apps —
	// the storefront silently showed nothing when this only knew the CRM's.
	const candidate = error as {
		apiError?: { data?: { paymentDecline?: PaymentDecline } };
		data?: { paymentDecline?: PaymentDecline };
	};
	const data =
		candidate.apiError?.data?.paymentDecline ?? candidate.data?.paymentDecline;

	return data != null && typeof data.message === 'string' ? data : null;
}
